import { useCallback, useEffect, useState } from "react";
import { PhoneForwarded, PhoneCall, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";

const POLL_MS = 4000;

function elapsed(startedAt) {
  if (!startedAt) return "";
  const s = Math.max(0, Math.floor((Date.now() - new Date(startedAt).getTime()) / 1000));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
}

function transferLine(t) {
  if (!t) return null;
  const name = t.to_name || "your colleague";
  switch (t.status) {
    case "queued":
    case "transferring":
      return { tone: "text-blue-700", text: `Ringing ${name}… stay on the line.`, busy: true };
    case "transferred":
      return { tone: "text-emerald-700", text: `${name} has joined. You can hang up now.` };
    case "transfer_failed":
      return { tone: "text-red-600", text: `Transfer failed: ${t.detail || "try again"}` };
    default:
      return null;
  }
}

/**
 * Shown while the signed-in employee is on a call carried by the phone
 * gateway. Lets them hand the customer to a colleague (warm transfer): the
 * gateway rings the colleague and merges them in, then the employee hangs up.
 */
export default function LiveCallBar() {
  const [live, setLive] = useState(null); // { call, transfer_targets }
  const [target, setTarget] = useState("");
  const [sending, setSending] = useState(false);
  const [, tick] = useState(0);

  const load = useCallback(async () => {
    if (document.visibilityState !== "visible") return;
    try {
      const { data } = await api.get("/calls/live");
      setLive(data?.call ? data : null);
    } catch (_) {
      // Network blip or logged out: keep whatever we last showed.
    }
  }, []);

  useEffect(() => {
    load();
    const poll = setInterval(load, POLL_MS);
    const clock = setInterval(() => tick((n) => n + 1), 1000);
    document.addEventListener("visibilitychange", load);
    return () => {
      clearInterval(poll);
      clearInterval(clock);
      document.removeEventListener("visibilitychange", load);
    };
  }, [load]);

  if (!live?.call) return null;
  const { call, transfer_targets: targets = [] } = live;
  const status = transferLine(call.transfer);
  const inFlight = status?.busy;

  const transfer = async () => {
    if (!target) return;
    setSending(true);
    try {
      const { data } = await api.post(`/calls/${call.id}/transfer`, { employee_id: target });
      toast.success(data?.message || "Transferring…");
      setTarget("");
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't start the transfer");
    } finally {
      setSending(false);
    }
  };

  return (
    <div
      className="mx-4 mt-3 rounded-2xl border border-emerald-200 bg-emerald-50 p-3 shadow-sm"
      data-testid="live-call-bar"
    >
      <div className="flex items-center gap-2">
        <PhoneCall className="h-4 w-4 text-emerald-700" />
        <div className="flex-1 min-w-0">
          <div className="text-sm font-semibold truncate">
            On a call · {call.customer_name || call.customer_phone || "customer"}
          </div>
          <div className="text-xs text-muted-foreground">
            {call.direction === "inbound" ? "Incoming" : "Outgoing"} · {elapsed(call.started_at)}
          </div>
        </div>
      </div>

      {status && (
        <div className={`mt-2 text-xs font-medium ${status.tone}`} data-testid="live-call-transfer-status">
          {status.text}
        </div>
      )}

      {call.handed_over ? null : targets.length > 0 ? (
        <div className="mt-2 flex gap-2">
          <select
            className="flex-1 min-w-0 rounded-lg border bg-white px-2 py-2 text-sm"
            value={target}
            onChange={(e) => setTarget(e.target.value)}
            disabled={inFlight || sending}
            data-testid="live-call-transfer-target"
          >
            <option value="">Transfer to…</option>
            {targets.map((t) => (
              <option key={t.employee_id} value={t.employee_id}>
                {t.name}
              </option>
            ))}
          </select>
          <Button
            size="sm"
            onClick={transfer}
            disabled={!target || inFlight || sending}
            data-testid="live-call-transfer-btn"
          >
            {sending || inFlight ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <PhoneForwarded className="h-4 w-4" />
            )}
            <span className="ml-1">Transfer</span>
          </Button>
        </div>
      ) : (
        <div className="mt-2 text-xs text-muted-foreground">
          No colleagues with a phone number to transfer to.
        </div>
      )}
    </div>
  );
}
